/**
 * @module components/layout/AppShell
 * @summary The main editor layout: navbar / toolbar / [library · canvas ·
 *   properties] / status bar, plus modals.
 * @description Responsive: on desktop the library and properties are docked
 *   side rails; on tablet-portrait/phone they become slide-over drawers toggled
 *   by floating buttons (the canvas stays full-bleed underneath). See
 *   docs/TECHNICAL-GUIDE.md → Responsive.
 * @ported-from JCC PFD Engineering Tool v1.1.html lines 409-558 (shell layout).
 */
import { Suspense, lazy, useEffect, useState } from 'react';
import { AppNavbar, type Panel } from '../auth/AppNavbar';
import { engine } from '../../engine/CanvasEngine';
import { TopBar, type EditorView } from '../toolbar/TopBar';
import { LibraryPanel } from '../library/LibraryPanel';
import { CanvasView } from '../canvas/CanvasView';
import { Canvas3DView } from '../canvas3d/Canvas3DView';
import { SolidView } from '../solid/SolidView';
import { PropertiesPanel } from '../properties/PropertiesPanel';
import { StatusBar } from '../statusbar/StatusBar';
import { Toast } from '../modals/Toast';
import { ContextMenu } from '../modals/ContextMenu';
import { HelpModal } from '../modals/HelpModal';
import { PdfExportModal } from '../modals/PdfExportModal';
import { Pdf3dExportModal } from '../modals/Pdf3dExportModal';
import { SymbolsPanel } from '../symbols/SymbolsPanel';
import { SymbolPasswordGate } from '../symbols/SymbolPasswordGate';
import { UserManagementPanel } from '../auth/UserManagementPanel';
import { useBreakpoint } from '../../hooks/useBreakpoint';
import { useEngineSync } from '../../hooks/useEngineSync';
import { useAuthStore } from '../../state/authStore';

/**
 * The materials tab carries ~130 KB of raw-material data. It is reference
 * reading, not part of drawing, so it loads the first time someone opens it
 * rather than sitting in the editor's bundle.
 */
const MaterialsPanel = lazy(() =>
  import('../materials/MaterialsPanel').then((m) => ({ default: m.MaterialsPanel })),
);

/** The formulation sheet, on the same footing. */
const FormulationPanel = lazy(() =>
  import('../formulation/FormulationPanel').then((m) => ({ default: m.FormulationPanel })),
);

/**
 * The calculation set, on the same footing — and the one that most needs to be
 * kept out of the editor's bundle: 295 calculations is the largest data module
 * in the app, and an engineer who never opens the tab never downloads it.
 */
const LineCalcsPanel = lazy(() =>
  import('../linecalcs/LineCalcsPanel').then((m) => ({ default: m.LineCalcsPanel })),
);

/** The co-kneader simulator, on the same footing. */
const LineSimPanel = lazy(() =>
  import('../linesim/LineSimPanel').then((m) => ({ default: m.LineSimPanel })),
);

/** Slide-over drawer wrapper used on small screens. */
function Drawer({
  side,
  open,
  onClose,
  children,
}: {
  side: 'left' | 'right';
  open: boolean;
  onClose: () => void;
  children: React.ReactNode;
}) {
  return (
    <>
      {open && (
        <div
          onClick={onClose}
          style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.3)', zIndex: 70 }}
        />
      )}
      <div
        style={{
          position: 'fixed',
          top: 'var(--navbar-h)',
          bottom: 'var(--statusbar-h)',
          [side]: 0,
          zIndex: 71,
          transform: open ? 'translateX(0)' : `translateX(${side === 'left' ? '-' : ''}110%)`,
          transition: 'transform 0.2s ease',
          boxShadow: '0 0 24px rgba(0,0,0,0.25)',
        }}
      >
        {children}
      </div>
    </>
  );
}

export function AppShell() {
  useEngineSync();
  const { isSmall } = useBreakpoint();
  const user = useAuthStore((s) => s.user);
  const [help, setHelp] = useState(false);
  /**
   * Which export dialog is open. The two are separate documents — a measurable
   * layout sheet set and a 3D view portfolio — so they get separate dialogs, and
   * the toolbar button opens whichever matches the view you are looking at.
   */
  const [pdfOpen, setPdfOpen] = useState<null | '2d' | '3d'>(null);
  const [libOpen, setLibOpen] = useState(false);
  const [propsOpen, setPropsOpen] = useState(false);
  const [panel, setPanel] = useState<Panel>('tool');
  const [gate, setGate] = useState(false);
  /** Which surface is on top: the 2D drawing, the 3D plant view, or the
   *  part modeller. */
  const [view, setView] = useState<EditorView>('2d');

  // Canvas shortcuts (Delete, Ctrl+Z, V/C/H, …) must not reach the hidden
  // canvas while a full-screen overlay or modal is open — nor while the 3D view
  // or the part modeller is covering it, where Delete would silently edit an
  // invisible drawing.
  useEffect(() => {
    engine.setShortcutsEnabled(panel === 'tool' && !help && !gate && view === '2d');
  }, [panel, help, gate, view]);

  // Engineers must pass the symbol-library password gate; managers/admins don't.
  const navigate = (p: Panel) => {
    if (p === 'symbols' && user?.role === 'engineer') {
      setGate(true);
      return;
    }
    setPanel(p);
  };

  return (
    <>
      <AppNavbar active={panel} onNavigate={navigate} />
      <TopBar
        onHelp={() => setHelp(true)}
        onExportPdf={() => setPdfOpen(view === '3d' ? '3d' : '2d')}
        view={view}
        onToggleView={() => setView((v) => (v === '3d' ? '2d' : '3d'))}
        onToggleSolid={() => setView((v) => (v === 'solid' ? '2d' : 'solid'))}
      />

      <div id="main">
        {isSmall ? (
          <Drawer side="left" open={libOpen} onClose={() => setLibOpen(false)}>
            <LibraryPanel />
          </Drawer>
        ) : (
          <LibraryPanel />
        )}

        <CanvasView />

        {isSmall ? (
          <Drawer side="right" open={propsOpen} onClose={() => setPropsOpen(false)}>
            <PropertiesPanel />
          </Drawer>
        ) : (
          <PropertiesPanel />
        )}

        {/* The 3D view is an OVERLAY, not a replacement: the 2D canvas and its
            engine stay mounted underneath with all their state intact, so
            switching back is instant and nothing about 2D behaviour changes. */}
        {view === '3d' && <Canvas3DView onExportPdf={() => setPdfOpen('3d')} />}

        {/* The part modeller, on the same overlay footing. It holds its OWN
            document, so switching away and back does not lose a part. */}
        {view === 'solid' && <SolidView />}
      </div>

      <StatusBar />

      {/* Floating drawer toggles (small screens only). Hidden in the part
          modeller, whose own panels occupy the bottom of a narrow screen — a
          2D drawer sliding over them would be editing the wrong document. */}
      {isSmall && view !== 'solid' && (
        <div
          style={{
            position: 'fixed',
            bottom: 'calc(28px + var(--safe-bottom))',
            left: '50%',
            transform: 'translateX(-50%)',
            display: 'flex',
            gap: 8,
            zIndex: 60,
          }}
        >
          <button className="fab-btn" onClick={() => setLibOpen((v) => !v)}>
            ▤ Shapes
          </button>
          <button className="fab-btn" onClick={() => setPropsOpen((v) => !v)}>
            ⚙ Properties
          </button>
        </div>
      )}

      {/* Full-screen overlays — the editor stays mounted underneath so the
          canvas engine keeps running. */}
      {panel === 'formulation' && (
        <Suspense fallback={<div className="overlay-panel open" />}>
          <FormulationPanel onClose={() => setPanel('tool')} />
        </Suspense>
      )}
      {panel === 'calcs' && (
        <Suspense fallback={<div className="overlay-panel open" />}>
          <LineCalcsPanel onClose={() => setPanel('tool')} />
        </Suspense>
      )}
      {panel === 'sim' && (
        <Suspense fallback={<div className="overlay-panel open" />}>
          <LineSimPanel onClose={() => setPanel('tool')} />
        </Suspense>
      )}
      {panel === 'materials' && (
        <Suspense fallback={<div className="overlay-panel open" />}>
          <MaterialsPanel onClose={() => setPanel('tool')} />
        </Suspense>
      )}
      {panel === 'symbols' && <SymbolsPanel onClose={() => setPanel('tool')} />}
      {panel === 'users' && <UserManagementPanel onClose={() => setPanel('tool')} />}
      {gate && (
        <SymbolPasswordGate
          onUnlock={() => {
            setGate(false);
            setPanel('symbols');
          }}
          onCancel={() => setGate(false)}
        />
      )}

      <Toast />
      <ContextMenu />
      <HelpModal open={help} onClose={() => setHelp(false)} />
      <PdfExportModal open={pdfOpen === '2d'} onClose={() => setPdfOpen(null)} />
      <Pdf3dExportModal open={pdfOpen === '3d'} onClose={() => setPdfOpen(null)} />
    </>
  );
}
